import { pipe } from 'fp-ts/es6/pipeable';
import * as TE from 'fp-ts/es6/TaskEither';
import * as O from 'fp-ts/es6/Option';
import { jwtDecode } from 'jwt-decode';
const handleError = (reason) => new Error(JSON.stringify(reason, null, 2));
const redirect = (userManager, state) => () => {
    return pipe(TE.tryCatch(() => userManager.signinRedirect({ useReplaceToNavigate: true, state }), handleError), TE.chain(() => TE.left(new Error('Not logged in (redirected)'))));
};
/**
 * Define a token freshness window to ensure that we're not holding tokens right up
 * until they expire. This codebase doesn't have any concept of managing/refreshing
 * expiring tokens aside from error/reload.
 *
 * This corresponds to 30 minutes in seconds.
 */
const TOKEN_FRESHNESS_WINDOW = 1800;
const isValidToken = (jwtToken) => {
    try {
        const currentTime = Math.floor(new Date().getTime() / 1000);
        const decodedToken = jwtDecode(jwtToken);
        const isNotExpired = decodedToken.exp > currentTime;
        const isFresh = decodedToken.iat + TOKEN_FRESHNESS_WINDOW > currentTime; // iat is the "issued at time" which updates every time a new token is generated
        return isNotExpired && isFresh;
    }
    catch (_a) {
        return false;
    }
};
/**
 * This will fetch and return the user data from UserManager storage (if available and valid).
 */
const getValidUser = (userManager, state) => pipe(TE.tryCatch(() => userManager.getUser(), handleError), TE.map((user) => {
    /**
     * Check if UserManager found a preexisting, valid user
     *
     * Note: for whatever reason oidc-client doesn't expose the issued at or expiry times
     * to us, so we're sourcing them directly from the jwt id_token.
     */
    if (user && isValidToken(user.id_token)) {
        user.state = state;
        return user;
    }
    // Otherwise return null
    else {
        return null;
    }
}));
const login = (userManager, state) => pipe(TE.tryCatch(() => userManager.signinCallback(), handleError), TE.alt(redirect(userManager, state)));
export const verifyUser = (userManager, state, user, onSignIn) => pipe(O.fromNullable(user), TE.fromOption(() => new Error('Not logged in')), TE.alt(() => pipe(
// Check if the UserManager already has a valid user stored
getValidUser(userManager, state), 
// If we still don't have a user, do a login
TE.chain((user) => (user ? TE.right(user) : login(userManager, state))), 
// Notify the caller via callback
TE.map((user) => {
    onSignIn(user);
    return user;
}))));
